import type {
  ChannelsResponse,
  UsersSearchResponse,
  ChannelMessagesResponse,
  SendMessageRequest,
  SendMessageResponse,
  CreateChannelRequest,
  CreateChannelResponse,
  UpdateChannelNameRequest,
  UpdateChannelNameResponse,
  LeaveChannelResponse,
  Channel,
  ChannelMember,
  ChannelMessage,
  SearchUserType,
  ChatContextType,
} from "@/features/chat/types";
import { fetcher } from "@/shared/lib/fetcher.client";

// --- New Chat Room API types (internal) ---

type RoomMember = {
  id: string
  first_name: string
  last_name: string
  initials: string
  avatar_url: string | null
  online: boolean
  member_type: "Core::Representative" | "Core::Employee"
};

type RoomLastMessage = {
  id: string
  content: string
  created_at: string
  author_id: string
};

type RoomContext = {
  type: string
  id: string
  title: string
};

type Room = {
  id: string
  name: string
  room_type: "direct" | "group"
  unread_count: number
  updated_at: string
  created_at: string
  members: RoomMember[]
  last_message?: RoomLastMessage
  context?: RoomContext
};

type RoomMessageAuthor = {
  id: string
  first_name: string
  last_name: string
  initials: string
  avatar_url?: string | null
  type: string
};

type RoomMessageReadBy = {
  id: string
  first_name: string
  last_name: string
  initials: string
  read_at?: string
};

type RoomMessageAttachment = {
  id: number
  filename: string
  content_type: string
  byte_size: number
  url: string
};

type RoomMessage = {
  id: string
  content: string
  message_type: "text" | "sound" | "system"
  created_at: string
  edited_at: string | null
  deleted_at: string | null
  deleted_by: { id: string, type: string } | null
  author: RoomMessageAuthor
  read_by: RoomMessageReadBy[]
  attachments: RoomMessageAttachment[]
};

type ChatUser = {
  id: string
  first_name: string
  last_name: string
  initials: string
  avatar_url: string | null
  member_type: string
};

type ChatUsersPageMetadata = {
  total_items: number
  current_page: number
  total_pages: number
};

// --- Mappers: Room API → Legacy Channel types ---

const mapRoomMemberToChannelMember = (m: RoomMember): ChannelMember => ({
  id: m.id as unknown as number, // prefix_id stored as-is, cast for type compat
  fullname: `${m.first_name} ${m.last_name}`,
  type: m.member_type === "Core::Representative" ? "Élu" : "Salarié",
  avatar: m.avatar_url,
  online: m.online,
});

const mapRoomToChannel = (room: Room): Channel => ({
  id: room.id as unknown as number,
  name: room.room_type === "group" ? room.name : null,
  created_at: room.created_at,
  updated_at: room.updated_at,
  members: room.members.map(mapRoomMemberToChannelMember),
  last_message: room.last_message?.content ?? "",
  unread_messages_count: room.unread_count,
  context: room.context,
});

const mapRoomMessageToChannelMessage = (msg: RoomMessage): ChannelMessage => ({
  id: msg.id,
  content: msg.content,
  author: {
    id: msg.author.id,
    fullname: `${msg.author.first_name} ${msg.author.last_name}`,
    avatar: msg.author.avatar_url ?? null,
  },
  created_at: msg.created_at,
  updated_at: msg.edited_at ?? msg.created_at,
  edited_at: msg.edited_at,
  system: msg.message_type === "system",
  deleted_at: msg.deleted_at,
  deleted_by: msg.deleted_by,
  attachments: msg.attachments.map((att) => ({
    id: att.id,
    filename: att.filename,
    content_type: att.content_type,
    byte_size: att.byte_size,
    url: att.url,
  })),
  read_by: msg.read_by.map((r) => ({
    id: r.id,
    first_name: r.first_name,
    last_name: r.last_name,
    initials: r.initials,
    read_at: r.read_at,
  })),
});

const mapChatUserToSearchUser = (u: ChatUser) => ({
  id: u.id as unknown as number,
  username: `${u.first_name} ${u.last_name}`,
  type: u.member_type === "Core::Representative" ? "Élu" : "Salarié",
  avatar: null,
  online: false,
});

// --- Service ---

export const chatService = {
  async getChannels(params?: { contextType?: ChatContextType }): Promise<ChannelsResponse> {
    const response = await fetcher.get<{ rooms: Room[] }>("/chat/rooms", { params: params?.contextType ? { context_type: params.contextType } : undefined });
    return {
      channels: response.data.rooms.map(mapRoomToChannel),
      metadata: { total_items: response.data.rooms.length, current_page: 1, total_pages: 1 },
    };
  },

  async getChannelById(channelId: number | string): Promise<Channel> {
    const response = await fetcher.get<{ room: Room }>(`/chat/rooms/${channelId}`);
    return mapRoomToChannel(response.data.room);
  },

  async searchUsers(searchQuery: string, page: number = 1, types?: SearchUserType[]): Promise<UsersSearchResponse> {
    // Search is handled server-side now (API paginates and accepts ?q=).
    // We aggregate the two groups (representatives / employees) into one
    // flat list client-side and collapse their pagination metadata by
    // taking the max page count and summing totals.
    const response = await fetcher.get<{
      representatives: ChatUser[]
      employees: ChatUser[]
      metadata?: { representatives: ChatUsersPageMetadata, employees: ChatUsersPageMetadata }
    }>("/chat/users", {
      params: {
        q: searchQuery.length > 0 ? searchQuery : undefined,
        page,
      },
    });
    let allUsers = [...response.data.representatives, ...response.data.employees];

    // Optional client-side type filter (Élu / Salarié).
    if (types && types.length > 0) {
      allUsers = allUsers.filter((u) => {
        if (types.includes("Élu") && u.member_type === "Core::Representative") return true;
        if (types.includes("Salarié") && u.member_type === "Core::Employee") return true;
        return false;
      });
    }

    // When the caller filters by a single type, only that bucket's pagination
    // metadata is meaningful — taking the max across both buckets would keep
    // the infinite scroll firing on pages that are exhausted for the type
    // we care about (e.g. fetching page 14 of employees while the user only
    // wants the 23 reps, all client-side filtered out).
    const meta = response.data.metadata;
    const onlyReps = types?.length === 1 && types[0] === "Élu";
    const onlyEmployees = types?.length === 1 && types[0] === "Salarié";
    const aggregated: ChatUsersPageMetadata = meta
      ? onlyReps
        ? meta.representatives
        : onlyEmployees
          ? meta.employees
          : {
            total_items: meta.representatives.total_items + meta.employees.total_items,
            current_page: Math.max(meta.representatives.current_page, meta.employees.current_page),
            total_pages: Math.max(meta.representatives.total_pages, meta.employees.total_pages),
          }
      : { total_items: allUsers.length, current_page: 1, total_pages: 1 };

    return {
      users: allUsers.map(mapChatUserToSearchUser),
      metadata: aggregated,
    };
  },

  async getChannelMessages(channelId: number | string): Promise<ChannelMessagesResponse> {
    const response = await fetcher.get<{ messages: RoomMessage[], last_read_at: string | null }>(
      `/chat/rooms/${channelId}/messages`,
    );
    return {
      messages: response.data.messages.map(mapRoomMessageToChannelMessage),
      metadata: { total_items: response.data.messages.length, current_page: 1, total_pages: 1 },
      last_read_at: response.data.last_read_at,
    };
  },

  async sendMessage(channelId: string, messageData: SendMessageRequest): Promise<SendMessageResponse> {
    if (messageData.attachments?.length) {
      const formData = new FormData();
      formData.append("message[content]", messageData.content);
      messageData.attachments.forEach((file) => {
        formData.append("message[attachments][]", file);
      });

      // The shared fetcher defaults to `Content-Type: application/json`. Without
      // overriding it here, axios won't switch to `multipart/form-data` (with
      // boundary) on its own and the Rails endpoint can't parse the FormData
      // payload — the attachment is dropped, and an attachment-only send is
      // rejected as 422 because the content slot ends up empty.
      const response = await fetcher.post<{ message: RoomMessage }>(
        `/chat/rooms/${channelId}/messages`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return mapRoomMessageToChannelMessage(response.data.message);
    }

    const response = await fetcher.post<{ message: RoomMessage }>(
      `/chat/rooms/${channelId}/messages`,
      { message: { content: messageData.content } },
    );
    return mapRoomMessageToChannelMessage(response.data.message);
  },

  async createChannel(data: CreateChannelRequest): Promise<CreateChannelResponse> {
    // Map legacy format to new Room API
    const memberIds = data.channel.channel_users_attributes.map((a) => a.user_id);

    // Create room
    const roomResponse = await fetcher.post<{ room: Room }>("/chat/rooms", {
      room: {
        member_ids: memberIds,
        name: data.channel.name,
      },
    });

    const room = roomResponse.data.room;

    // Send initial message if provided
    if (data.channel.message) {
      await fetcher.post(`/chat/rooms/${room.id}/messages`, { message: { content: data.channel.message } });
    }

    // Re-fetch to get updated room with message
    const updatedResponse = await fetcher.get<{ room: Room }>(`/chat/rooms/${room.id}`);

    return { channel: mapRoomToChannel(updatedResponse.data.room) };
  },

  async updateChannelName(channelId: number, data: UpdateChannelNameRequest): Promise<UpdateChannelNameResponse> {
    const response = await fetcher.patch<{ room: Room }>(`/chat/rooms/${channelId}`, { room: { name: data.name } });
    return { channel: mapRoomToChannel(response.data.room) };
  },

  async leaveChannel(channelId: number | string): Promise<LeaveChannelResponse> {
    await fetcher.delete(`/chat/rooms/${channelId}/leave`);
    return { content: "left" };
  },

  async markAsRead(channelId: string): Promise<void> {
    await fetcher.patch(`/chat/rooms/${channelId}/mark_as_read`);
  },

  async deleteMessage(channelId: string, messageId: string, mode: "for_me" | "for_everyone" = "for_me"): Promise<void> {
    await fetcher.delete(`/chat/rooms/${channelId}/messages/${messageId}`, { params: { mode } });
  },

  async editMessage(channelId: string, messageId: string, content: string): Promise<{ message: RoomMessage }> {
    const response = await fetcher.patch<{ message: RoomMessage }>(
      `/chat/rooms/${channelId}/messages/${messageId}`,
      { message: { content } },
    );
    return response.data;
  },

  async addMembers(channelId: number | string, memberIds: string[]): Promise<{ room: Room }> {
    const response = await fetcher.post<{ room: Room }>(
      `/chat/rooms/${channelId}/add_members`,
      { member_ids: memberIds },
    );
    return response.data;
  },
};
